import { Check, ArrowRight } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";

const plans = [
  {
    name: "Starter",
    price: 39,
    description: "For small teams getting started.",
    features: [
      "2 feedback pages",
      "100 anonymous submissions / month",
      "Basic AI sentiment analysis",
      "Email support (48h)",
    ],
    popular: false,
  },
  {
    name: "Professional",
    price: 79,
    description: "For growing teams with advanced needs.",
    features: [
      "10 feedback pages",
      "Unlimited anonymous submissions",
      "Advanced AI insights & theme clustering",
      "Action tracker with kanban",
      "Priority email support (24h)",
    ],
    popular: true,
  },
  {
    name: "Business",
    price: 149,
    description: "Enterprise-grade features and support.",
    features: [
      "Unlimited everything",
      "HR Advisor AI + custom training",
      "Custom domain & white-label",
      "Full API access & webhooks",
      "Priority support (4h response)",
    ],
    popular: false,
  },
];

export const PricingPreview = () => {
  const navigate = useNavigate();

  return (
    <section className="py-20 md:py-28 px-4 sm:px-6 md:px-10">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-12 md:mb-16 max-w-3xl">
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-flex h-2 w-2 rounded-full bg-primary" />
            <span className="font-sans text-[11px] uppercase tracking-[0.22em] font-medium text-muted-foreground">
              The Pricing
            </span>
          </div>
          <h2 className="font-sans text-3xl md:text-5xl font-medium tracking-[-0.02em] text-foreground leading-[1.1]">
            Built for{" "}
            <span className="font-serif italic font-normal text-primary">HR budgets.</span>
          </h2>
          <p className="font-sans text-[15px] md:text-[17px] text-muted-foreground mt-5 leading-relaxed">
            Flat monthly pricing. No per-employee fees. No annual contracts.
          </p>
        </div>

        {/* Plans */}
        <div className="grid md:grid-cols-3 gap-5 md:gap-6 items-stretch">
          {plans.map((plan, i) => {
            const isPopular = plan.popular;
            return (
              <article
                key={plan.name}
                className={`group relative flex flex-col rounded-3xl p-8 md:p-9 h-full transition-all duration-300 hover:-translate-y-1 ${
                  isPopular
                    ? "bg-foreground text-background border border-foreground shadow-[0_24px_60px_-20px_rgba(21,34,27,0.4)] hover:shadow-[0_28px_70px_-20px_rgba(21,34,27,0.5)]"
                    : "bg-card/90 backdrop-blur-sm border border-border/60 hover:border-border hover:shadow-[0_24px_60px_-24px_rgba(62,92,77,0.18)]"
                }`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-1 rounded-full font-sans text-[10px] uppercase tracking-[0.22em] font-medium">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                    Most Popular
                  </div>
                )}

                {/* Plan label */}
                <div className="flex items-center justify-between mb-5">
                  <span
                    className={`font-sans text-[10px] uppercase tracking-[0.22em] font-medium tabular-nums ${
                      isPopular ? "text-background/60" : "text-muted-foreground"
                    }`}
                  >
                    Plan {String(i + 1).padStart(2, "0")}
                  </span>
                </div>

                {/* Name + description */}
                <h3
                  className={`font-sans text-2xl md:text-[28px] font-medium tracking-[-0.015em] leading-tight mb-2 ${
                    isPopular ? "text-background" : "text-foreground"
                  }`}
                >
                  {plan.name}
                </h3>
                <p
                  className={`font-sans text-[14px] leading-relaxed mb-7 ${
                    isPopular ? "text-background/70" : "text-muted-foreground"
                  }`}
                >
                  {plan.description}
                </p>

                {/* Price */}
                <div
                  className={`flex items-baseline gap-1.5 pb-7 mb-7 border-b ${
                    isPopular ? "border-background/15" : "border-border/60"
                  }`}
                >
                  <span
                    className={`font-sans text-5xl md:text-[56px] font-medium tracking-[-0.025em] leading-none tabular-nums ${
                      isPopular ? "text-background" : "text-foreground"
                    }`}
                  >
                    ${plan.price}
                  </span>
                  <span
                    className={`font-sans text-[13px] font-medium ${
                      isPopular ? "text-background/60" : "text-muted-foreground"
                    }`}
                  >
                    / month
                  </span>
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3">
                      <span
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${
                          isPopular
                            ? "bg-background/15 text-background"
                            : "bg-primary/10 text-primary"
                        }`}
                      >
                        <Check className="h-3 w-3" strokeWidth={2.5} />
                      </span>
                      <span
                        className={`font-sans text-[14.5px] leading-relaxed ${
                          isPopular ? "text-background/90" : "text-foreground"
                        }`}
                      >
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={() => navigate("/auth?mode=signup")}
                  className={`group/btn w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full font-sans text-[14px] font-medium tracking-[-0.005em] transition-all duration-300 min-h-[52px] mt-auto ${
                    isPopular
                      ? "bg-primary text-primary-foreground hover:bg-primary/90"
                      : "bg-foreground text-background hover:bg-foreground/90"
                  }`}
                >
                  {isPopular ? "Start with Professional" : `Choose ${plan.name}`}
                  <ArrowRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5" strokeWidth={1.75} />
                </button>
              </article>
            );
          })}
        </div>

        {/* Footer link */}
        <div className="text-center mt-12 md:mt-14">
          <Link
            to="/pricing"
            className="group inline-flex items-center gap-2 font-sans text-[13px] font-medium tracking-[-0.005em] text-foreground hover:text-primary transition-colors min-h-[44px]"
          >
            View full feature comparison
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.75} />
          </Link>
        </div>
      </div>
    </section>
  );
};
